import { Link } from "react-router-dom";
import { Download, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { stageLabel } from "@/lib/stages";

const COLS = ["name", "email", "client", "account", "service_line", "job", "job_type", "status", "hired", "invited_at", "hired_at", "phone", "video", "coding", "overall", "recommendation"];
const STATUS_CHIP = { invited: "tp-chip", screening: "tp-chip tp-chip-indigo", incomplete: "tp-chip tp-chip-amber", completed: "tp-chip tp-chip-emerald" };

const csvCell = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

function downloadCsv(rows, title) {
  const lines = [COLS.map((c) => c.replace("_", " ").replace(/\b\w/g, (m) => m.toUpperCase())).join(","),
    ...rows.map((r) => COLS.map((c) => csvCell(c === "hired" ? (r.hired ? "Yes" : "No") : c === "status" ? stageLabel(r.status) : r[c])).join(","))];
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

const Score = ({ v }) => <span className={`font-mono text-xs ${v == null ? "text-slate-300" : v >= 70 ? "text-emerald-700" : v >= 40 ? "text-amber-700" : "text-rose-700"}`}>{v ?? "—"}</span>;

export default function DrillPanel({ drill, rows, period, onClose }) {
  const list = drill ? rows.filter(drill.test) : [];
  return (
    <Sheet open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col bg-white" data-testid="drill-panel">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100 text-left">
          <SheetTitle className="font-display text-lg text-slate-900 pr-8" data-testid="drill-title">{drill?.title}</SheetTitle>
          <SheetDescription className="text-xs text-slate-500">{period}</SheetDescription>
          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-slate-700" data-testid="drill-count"><b>{list.length}</b> candidate{list.length === 1 ? "" : "s"}</span>
            <button onClick={() => downloadCsv(list, drill?.title || "candidates")} disabled={!list.length} data-testid="drill-download-csv"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <Download className="w-4 h-4" /> Download CSV
            </button>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto">
          {!list.length ? <div className="text-sm text-slate-400 text-center py-16">No candidates here</div> : (
            <ul className="divide-y divide-slate-100">
              {list.map((r) => (
                <li key={r.id} className="px-6 py-3 hover:bg-slate-50/70" data-testid={`drill-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/app/candidates/${r.id}`} target="_blank" className="font-medium text-slate-900 hover:text-indigo-600 inline-flex items-center gap-1" data-testid={`drill-open-${r.id}`}>
                        {r.name} <ExternalLink className="w-3 h-3 text-slate-400" />
                      </Link>
                      <div className="text-xs text-slate-500 truncate">{r.job} · {r.client}{r.account !== "No account" ? ` · ${r.account}` : ""}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">Invited {r.invited_at}{r.hired_at ? ` · Hired ${r.hired_at}` : ""}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <div className="flex gap-1">
                        <span className={`${STATUS_CHIP[r.status]} text-[10px]`}>{stageLabel(r.status)}</span>
                        {r.hired && <span className="tp-chip tp-chip-emerald text-[10px]">Hired</span>}
                      </div>
                      {r.recommendation && <span className="text-[11px] text-slate-600">{r.recommendation}</span>}
                    </div>
                  </div>
                  <div className="flex gap-4 mt-1.5 text-[11px] text-slate-500">
                    {r.rounds.includes("Phone") && <span>Phone <Score v={r.phone} /></span>}
                    {r.rounds.includes("Video") && <span>Video <Score v={r.video} /></span>}
                    {r.rounds.includes("Coding") && <span>Coding <Score v={r.coding} /></span>}
                    <span>Overall <Score v={r.overall} /></span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
